<template>
  <div>
    <b-card>
      <b-row>
        <div class="col-12 mt-16">
          <b-form @submit.prevent="addUser" v-if="show">
            <!-- Section Heading -->
            <div style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;">
              <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
                User Management Information
              </h4>
            </div>

            <div class="row">
              <!-- Dynamic Fields -->
              <div
                class="col-md-4 col-12"
                v-for="(field, key) in fields"
                :key="key"
              >
                <b-form-group
                  :label="field.label"
                  :label-for="key"
                  label-class="font-weight-bold"
                >
                  <template v-if="key === 'country'">
                    <v-select
                      :id="key"
                      v-model="selectedCountry"
                      :options="countryOptions"
                      label="name"
                      track-by="isoCode"
                      placeholder="Select country"
                      :clearable="true"
                      :filterable="true"
                    />
                  </template>
                  <template v-else-if="key === 'city'">
                    <v-select
                      :id="key"
                      v-model="selectedCity"
                      :options="cityOptions"
                      label="name"
                      track-by="id"
                      placeholder="Select city"
                      :clearable="true"
                      :filterable="true"
                      :disabled="!selectedCountry || !cityOptions.length"
                    />
                    <b-form-invalid-feedback v-if="selectedCountry && !cityOptions.length" class="d-block">
                      No cities available for the selected country.
                    </b-form-invalid-feedback>
                  </template>
                  <template v-else>
                    <b-form-input
                      :id="key"
                      :placeholder="field.placeholder"
                      v-model="formData[key]"
                      :state="fieldStates[key]"
                    />
                  </template>
                  <b-form-invalid-feedback v-if="fieldErrors[key]" class="d-block text-danger">
                    {{ fieldErrors[key] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </div>

              <!-- Marketplace Selection -->
              <div class="col-md-4 col-12">
                <b-form-group
                  label="Assign Marketplace Vehicles:"
                  label-class="font-weight-bold"
                >
                  <MultiSelect
                    id="marketplace_id"
                    v-model="marketplace_id"
                    :options="markerplaces"
                    optionLabel="text"
                    optionValue="value"
                    filter
                    placeholder="Select Marketplace Vehicle"
                    :maxSelectedLabels="1"
                    class="fixed-width"
                  />
                </b-form-group>
              </div>

              <!-- Picture Upload -->
              <div class="col-md-4 col-12">
                <b-form-group
                  label="Profile Picture:"
                  label-class="font-weight-bold"
                  description="Recommended size: 300x300px (JPG/PNG)"
                >
                  <!-- Current Image -->
                  <div v-if="image && !newImagePreview" class="mb-2">
                    <small class="text-muted">Current Picture:</small><br />
                    <img
                      :src="'https://wossdb.wossautoid.com/' + image"
                      alt="Current Picture"
                      class="img-thumbnail"
                      style="max-height: 120px;"
                    />
                  </div>

                  <!-- Upload Area -->
                  <div
                    class="border rounded p-3 text-center"
                    style="cursor: pointer; background: #f9f9f9;"
                    @drop.prevent="onImageDrop"
                    @dragover.prevent
                    @click="$refs.profileInput.click()"
                  >
                    <div v-if="!newImagePreview">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <p class="mb-0">Drag & Drop or Click to Upload</p>
                    </div>
                    <div v-else>
                      <img
                        :src="newImagePreview"
                        alt="Preview"
                        class="img-thumbnail"
                        style="max-height: 120px;"
                      />
                    </div>
                    <input
                      ref="profileInput"
                      type="file"
                      accept="image/png, image/jpeg"
                      class="d-none"
                      @change="onSingleImageChange"
                    />
                  </div>
                  <small class="text-muted">Leave empty to keep current image</small>
                  <b-form-invalid-feedback v-if="profileError" class="d-block text-danger">
                    {{ profileError }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </div>
            </div>

            <!-- Submit -->
            <b-button type="submit" variant="primary" class="mb-8 mr-8" :disabled="isLoading">
              <span v-if="!isLoading">Submit</span>
              <b-spinner v-else class="mb-8 mr-8" variant="primary" small></b-spinner>
            </b-button>
          </b-form>
        </div>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BFormInvalidFeedback,
} from "bootstrap-vue";
import axios from "../../../axios";
import MultiSelect from "primevue/multiselect";
import imageCompression from "browser-image-compression";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { Country, City } from "country-state-city";

export default {
  data() {
    return {
      show: true,
      isLoading: false,
      image: "",
      newImage: null,
      newImagePreview: null,
      profileError: "",
      successMessage: "",
      marketplace_id: [],
      markerplaces: [],

      selectedCountry: null,
      selectedCity: null,
      countryOptions: [],
      cityOptions: [],

      fields: {
        first_name: { label: "Name", placeholder: "Enter name" },
        email: { label: "Email", placeholder: "Enter email", type: "email" },
        description: { label: "Description", placeholder: "Enter description" },
        phone_number: { label: "Phone Number", placeholder: "Enter phone number", type: "number" },
        street_address: { label: "Address", placeholder: "Enter address" },
        city: { label: "City", placeholder: "Enter city" },
        state: { label: "State", placeholder: "Enter state" },
        country: { label: "Country", placeholder: "Enter country" },
      },

      formData: {
        first_name: "",
        email: "",
        description: "",
        phone_number: "",
        country: "",
        state: "",
        city: "",
        street_address: "",
      },

      fieldErrors: {},
    };
  },

  computed: {
    fieldStates() {
      const states = {};
      for (const key in this.fields) {
        states[key] = this.validateField(this.formData[key], this.fields[key].type || "text");
      }
      return states;
    },
  },

  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BFormInvalidFeedback,
    MultiSelect,
    vSelect,
  },

  mounted() {
    axios.get("marketplaceVehicleListforManager")
      .then((response) => {
        if (response.data.success && response.data.data) {
          this.markerplaces = response.data.data.map((m) => ({
            value: m.id,
            text: m.engine_number,
          }));
        }
      })
      .catch((error) => {
        console.error("Error fetching marketplace vehicles:", error);
      });
  },

  created() {
    this.countryOptions = Country.getAllCountries().map(({ name, isoCode }) => ({
      name,
      isoCode,
    }));
    const userId = this.$route.params.id;
    axios.get(`getManagementUserById/${userId}`)
      .then((response) => {
        const user = response.data.data;
        if (user) {
          Object.keys(this.formData).forEach((key) => {
            this.formData[key] = user[key] || "";
          });
          this.image = user.image || "";
          this.marketplace_id = Array.isArray(user.marketplaces)
            ? user.marketplaces.map((mp) => mp.id)
            : [];
          if (this.formData.country) {
            const matchedCountry = this.countryOptions.find((option) => option.name === this.formData.country);
            if (matchedCountry) {
              this.selectedCountry = matchedCountry;
              const matchedCities = City.getCitiesOfCountry(matchedCountry.isoCode).map(({ name, countryCode, stateCode }) => ({
                name,
                id: `${countryCode}-${stateCode || "NA"}-${name}`,
              }));
              this.cityOptions = matchedCities;
              if (this.formData.city) {
                const matchedCity = matchedCities.find((option) => option.name === this.formData.city);
                if (matchedCity) {
                  this.selectedCity = matchedCity;
                }
              }
            }
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
      });
  },

  methods: {
    validateField(value, type = "text") {
      if (value === null || value === undefined || value === "") return null;

      if (type === "email") {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(value);
      }
      if (type === "number") {
        const regex = /^\+?[0-9 ]+$/;
        return regex.test(value.toString().trim());
      }
      return value.toString().trim().length > 0;
    },

    async onSingleImageChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.profileError = "Only JPG/PNG allowed.";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.profileError = "Max size is 5MB.";
        return;
      }

      try {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 600, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        this.newImage = compressedFile;
        this.newImagePreview = URL.createObjectURL(compressedFile);
        this.profileError = "";
      } catch (err) {
        this.newImage = file;
        this.newImagePreview = URL.createObjectURL(file);
      }
    },

    onImageDrop(e) {
      const file = e.dataTransfer.files[0];
      this.onSingleImageChange({ target: { files: [file] } });
    },

    async addUser() {
      this.isLoading = true;
      this.fieldErrors = {};

      // Basic validation
      let isValid = true;
      for (const key in this.fields) {
        const valid = this.validateField(this.formData[key], this.fields[key].type || "text");
        if (!valid) {
          this.fieldErrors[key] = `${this.fields[key].label} is required or invalid.`;
          isValid = false;
        }
      }
      if (!isValid) {
        this.isLoading = false;
        return;
      }

      const submitData = new FormData();
      for (const key in this.formData) {
        submitData.append(key, this.formData[key]);
      }
      if (this.newImage) {
        submitData.append("image", this.newImage);
      }
      if (this.marketplace_id && Array.isArray(this.marketplace_id)) {
        this.marketplace_id.forEach((id) => {
          submitData.append("marketplace_id[]", id);
        });
      }

      const userId = this.$route.params.id;
      try {
        await axios.post(`updateManagementUser/${userId}`, submitData);
        this.$bvToast.toast("User updated successfully!", {
          title: "Success",
          variant: "success",
          solid: true,
          toaster: "b-toaster-top-right",
          autoHideDelay: 5000,
        });
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$bvToast.toast(
          error.response?.data?.message || "An error occurred while updating the user.",
          {
            title: "Error",
            variant: "danger",
            solid: true,
            toaster: "b-toaster-top-right",
            autoHideDelay: 5000,
          }
        );
      }
    },

    resetForm() {
      Object.keys(this.formData).forEach((key) => {
        this.formData[key] = "";
      });
      this.marketplace_id = [];
      this.newImage = null;
      this.newImagePreview = null;
      this.profileError = "";
      this.fieldErrors = {};
      this.selectedCountry = null;
      this.selectedCity = null;
      this.cityOptions = [];
    },
  },

  watch: {
    selectedCountry(newVal) {
      if (!newVal) {
        this.formData.country = "";
        this.formData.city = "";
        this.cityOptions = [];
        this.selectedCity = null;
        return;
      }
      this.formData.country = newVal.name;
      this.selectedCity = null;
      this.formData.city = "";
      this.cityOptions = City.getCitiesOfCountry(newVal.isoCode).map(({ name, countryCode, stateCode }) => ({
        name,
        id: `${countryCode}-${stateCode || "NA"}-${name}`,
      }));
    },
    selectedCity(newVal) {
      this.formData.city = newVal ? newVal.name : "";
    },
  },
};
</script>

<style scoped>
.fixed-width {
  width: 100%;
  border-radius: 6px;
}
</style>
